import { API_BASE_URL } from '@/config/api'
import { apiDeleteRequest, apiRequest, apiUploadRequest } from '@/services/api'
import type {
  AvatarMutationResponse,
  BackendUser,
  GetUserResponse,
  UpdateUserDto,
  UpdateUserResponse,
  UsersListResponse
} from '@/types'

function encodeId(id: string): string {
  return encodeURIComponent(id)
}

function nonEmptyString(v: unknown): string | null {
  if (typeof v !== 'string') return null
  const t = v.trim()
  return t ? t : null
}

/**
 * Caso normal (JR Finance): `photoUrl` já vem como URL HTTPS pública do R2 — retorna igual.
 * Só ajusta paths relativos raros (`/…`, `//…`) para não quebrar `<img src>`.
 */
export function absolutizePublicUrl(url: string): string {
  const t = url.trim()
  if (!t) return ''
  if (/^(https?:|data:|blob:)/i.test(t)) return t
  if (t.startsWith('//')) {
    return typeof window !== 'undefined' ? `${window.location.protocol}${t}` : `https:${t}`
  }
  if (t.startsWith('/')) {
    try {
      const base = new URL(API_BASE_URL, typeof window !== 'undefined' ? window.location.href : 'http://localhost/')
      return `${base.origin}${t}`
    } catch {
      return t
    }
  }
  return t
}

/** Unifica aliases da API (camelCase / snake_case) em `photoUrl` (fonte única para `<img>`). */
export function normalizeBackendUser(user: BackendUser): BackendUser {
  const r = user as unknown as Record<string, unknown>
  const photoRaw =
    nonEmptyString(user.photoUrl) ||
    nonEmptyString(r.photo_url) ||
    nonEmptyString(r.avatar_url) ||
    nonEmptyString(r.avatarUrl) ||
    nonEmptyString(r.profile_photo_url) ||
    nonEmptyString(r.profilePhotoUrl) ||
    nonEmptyString(r.picture)

  return {
    ...user,
    photoUrl: photoRaw ? absolutizePublicUrl(photoRaw) : null
  }
}

/** Formata CPF para o formato exigido pela API: 000.000.000-00 */
export function formatCpfForApi(cpfDigits: string): string {
  const d = cpfDigits.replace(/\D/g, '')
  if (d.length !== 11) {
    return cpfDigits
  }
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export const userService = {
  async listUsers(limit: number = 50, offset: number = 0): Promise<UsersListResponse> {
    const res = await apiRequest<UsersListResponse>(`/users?limit=${limit}&offset=${offset}`)
    return {
      ...res,
      users: res.users.map((u) => normalizeBackendUser(u))
    }
  },

  async getUserById(id: string): Promise<BackendUser> {
    const { user } = await apiRequest<GetUserResponse>(`/users/${encodeId(id)}`)
    return normalizeBackendUser(user)
  },

  async updateUser(id: string, data: UpdateUserDto): Promise<BackendUser> {
    const body: UpdateUserDto = { ...data }
    if (body.cpf) {
      const digits = body.cpf.replace(/\D/g, '')
      if (digits.length === 11) {
        body.cpf = formatCpfForApi(digits)
      }
    }
    const res = await apiRequest<UpdateUserResponse>(`/users/${encodeId(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body)
    })
    return normalizeBackendUser(res.user)
  },

  async deleteUser(id: string): Promise<void> {
    await apiRequest<{ message: string }>(`/users/${encodeId(id)}`, {
      method: 'DELETE'
    })
  },

  /**
   * PUT `${API_BASE_URL}/users/avatar` — multipart/form-data.
   * Campo do arquivo: `photo` (o Worker também aceita `file`).
   * Resposta: usuário atualizado; `photoUrl` é URL pública R2, não rota de download da API.
   */
  async uploadAvatar(file: File): Promise<BackendUser> {
    const res = await apiUploadRequest<AvatarMutationResponse>('/users/avatar', 'PUT', () => {
      const fd = new FormData()
      fd.append('photo', file)
      return fd
    })
    return normalizeBackendUser(res.user)
  },

  /**
   * DELETE `${API_BASE_URL}/users/avatar` — remove objeto no R2 e zera referência.
   * `photoUrl` na resposta tende a `null`. Sem GET de bytes pela API.
   */
  async deleteAvatar(fallbackUserId: string): Promise<BackendUser> {
    const res = await apiDeleteRequest<AvatarMutationResponse | { message: string } | null>('/users/avatar')
    if (res && typeof res === 'object' && 'user' in res && res.user) {
      return normalizeBackendUser(res.user)
    }
    return this.getUserById(fallbackUserId)
  }
}
